<template>
    <main
        class="w-full h-full min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="w-full flex min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-6xl lg:mx-auto"
        >
            <h1 id="primary-heading" class="sr-only">Admin</h1>
            <div class="px-4 sm:px-6 lg:px-8">
                <div class="sm:flex sm:items-center">
                    <div class="sm:flex-auto">
                        <profile-header-tabs current="general" />
                    </div>
                </div>
                <div class="my-4 mx-1">
                    <form @submit.prevent="saveProfile" class="space-y-6">
                        <div class="space-y-6">
                            <div
                                data-testid="profile-personal-section"
                                class="space-y-6 rounded-xl border border-taupe-200 bg-white p-6 sm:p-7"
                            >
                                <div>
                                    <h3
                                        class="text-lg font-medium leading-6 text-taupe-800"
                                    >
                                        Personal Information
                                    </h3>
                                    <p
                                        class="mt-1 max-w-2xl text-sm text-taupe-500"
                                    >
                                        Provide your personal information for a
                                        more customized experience in Zenmanage.
                                    </p>
                                </div>
                                <div class="space-y-6 sm:space-y-5">
                                    <div class="space-y-2 max-w-2xl">
                                        <label
                                            for="first-name"
                                            class="block text-sm font-medium text-taupe-800"
                                            >First name</label
                                        >
                                        <input
                                            type="text"
                                            v-model="data.first_name"
                                            name="first-name"
                                            id="first-name"
                                            autocomplete="given-name"
                                            class="block w-full rounded-md border-taupe-300 bg-white px-3 py-2 text-taupe-900 shadow-xs focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                        />
                                    </div>

                                    <div class="space-y-2 max-w-2xl">
                                        <label
                                            for="last-name"
                                            class="block text-sm font-medium text-taupe-800"
                                            >Last name</label
                                        >
                                        <input
                                            type="text"
                                            v-model="data.last_name"
                                            name="last-name"
                                            id="last-name"
                                            autocomplete="family-name"
                                            class="block w-full rounded-md border-taupe-300 bg-white px-3 py-2 text-taupe-900 shadow-xs focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                        />
                                    </div>

                                    <div class="space-y-2 max-w-2xl">
                                        <label
                                            for="email"
                                            class="block text-sm font-medium text-taupe-800"
                                            >Email address</label
                                        >
                                        <input
                                            v-model="data.email"
                                            id="email"
                                            name="email"
                                            type="email"
                                            autocomplete="email"
                                            class="block w-full rounded-md border-taupe-300 bg-white px-3 py-2 text-taupe-900 shadow-xs focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            <sign-in-methods />

                            <div
                                data-testid="profile-preferences-section"
                                class="space-y-6 rounded-xl border border-taupe-200 bg-white p-6 sm:p-7"
                            >
                                <div>
                                    <h3
                                        class="text-lg font-medium leading-6 text-taupe-800"
                                    >
                                        Project Preferences
                                    </h3>
                                    <p
                                        class="mt-1 max-w-2xl text-sm text-taupe-500"
                                    >
                                        Set your preferences below to customize
                                        your experience in Zenmanage.
                                    </p>
                                </div>
                                <div class="space-y-6 sm:space-y-5">
                                    <div class="space-y-2 max-w-2xl">
                                        <label
                                            for="default-project"
                                            class="block text-sm font-medium text-taupe-800"
                                            >Default project</label
                                        >
                                        <select
                                            v-model="data.default_project_key"
                                            name="default-project"
                                            id="default-project"
                                            class="block w-full rounded-md border-taupe-300 bg-white px-3 py-2 text-taupe-900 shadow-xs focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                        >
                                            <option disabled value="">
                                                Please select one
                                            </option>
                                            <option
                                                v-for="project in projects"
                                                :key="project.ulid"
                                                :value="project.key"
                                            >
                                                {{ project.name }}
                                            </option>
                                        </select>
                                    </div>

                                    <div class="space-y-2 max-w-2xl">
                                        <label
                                            for="default-environment"
                                            class="block text-sm font-medium text-taupe-800"
                                            >Default environment</label
                                        >
                                        <select
                                            v-model="
                                                data.default_environment_key
                                            "
                                            name="default-environment"
                                            id="default-environment"
                                            class="block w-full rounded-md border-taupe-300 bg-white px-3 py-2 text-taupe-900 shadow-xs focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                        >
                                            <option disabled value="">
                                                Please select one
                                            </option>
                                            <option
                                                v-for="environment in environments"
                                                :key="environment.ulid"
                                                :value="environment.key"
                                            >
                                                {{ environment.name }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div
                                data-testid="profile-datetime-section"
                                class="space-y-6 rounded-xl border border-taupe-200 bg-white p-6 sm:p-7"
                            >
                                <div>
                                    <h3
                                        class="text-lg font-medium leading-6 text-taupe-800"
                                    >
                                        Date & Time Preferences
                                    </h3>
                                    <p
                                        class="mt-1 max-w-2xl text-sm text-taupe-500"
                                    >
                                        Override the account defaults for
                                        timezone and date/time display.
                                    </p>
                                </div>
                                <div class="space-y-6 sm:space-y-5">
                                    <div class="space-y-2 max-w-2xl">
                                        <label
                                            for="timezone"
                                            class="block text-sm font-medium text-taupe-800"
                                            >Timezone</label
                                        >
                                        <p class="text-sm text-taupe-500">
                                            Override the account timezone for
                                            your personal view. Leave blank to
                                            use the account default.
                                        </p>
                                        <select
                                            v-model="data.timezone"
                                            name="timezone"
                                            id="timezone"
                                            class="block w-full rounded-md border-taupe-300 bg-white px-3 py-2 text-taupe-900 shadow-xs focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                        >
                                            <option :value="null">
                                                Use account default
                                            </option>
                                            <option
                                                v-for="tz in timezones"
                                                :key="tz"
                                                :value="tz"
                                            >
                                                {{ tz }}
                                            </option>
                                        </select>
                                    </div>

                                    <div class="space-y-2 max-w-2xl">
                                        <label
                                            for="date-format"
                                            class="block text-sm font-medium text-taupe-800"
                                            >Date format</label
                                        >
                                        <p class="text-sm text-taupe-500">
                                            Choose how dates are displayed.
                                            Leave blank to use the account
                                            default.
                                        </p>
                                        <select
                                            v-model="data.date_format"
                                            name="date-format"
                                            id="date-format"
                                            class="block w-full rounded-md border-taupe-300 bg-white px-3 py-2 text-taupe-900 shadow-xs focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                        >
                                            <option :value="null">
                                                Use account default
                                            </option>
                                            <option
                                                v-for="df in dateFormats"
                                                :key="df.value"
                                                :value="df.value"
                                            >
                                                {{
                                                    moment(Date.now()).format(
                                                        df.value
                                                    )
                                                }}
                                            </option>
                                        </select>
                                    </div>

                                    <div class="space-y-2 max-w-2xl">
                                        <label
                                            for="time-format"
                                            class="block text-sm font-medium text-taupe-800"
                                            >Time format</label
                                        >
                                        <p class="text-sm text-taupe-500">
                                            Choose how times are displayed.
                                            Leave blank to use the account
                                            default.
                                        </p>
                                        <select
                                            v-model="data.time_format"
                                            name="time-format"
                                            id="time-format"
                                            class="block w-full rounded-md border-taupe-300 bg-white px-3 py-2 text-taupe-900 shadow-xs focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                        >
                                            <option :value="null">
                                                Use account default
                                            </option>
                                            <option
                                                v-for="tf in timeFormats"
                                                :key="tf.value"
                                                :value="tf.value"
                                            >
                                                {{
                                                    moment(Date.now()).format(
                                                        tf.value
                                                    )
                                                }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div
                                data-testid="profile-newsletter-section"
                                class="space-y-6 rounded-xl border border-taupe-200 bg-white p-6 sm:p-7"
                            >
                                <div>
                                    <h3
                                        class="text-lg font-medium leading-6 text-taupe-800"
                                    >
                                        Communication Preferences
                                    </h3>
                                    <p
                                        class="mt-1 max-w-2xl text-sm text-taupe-500"
                                    >
                                        Occasionally receive tips and news.
                                    </p>
                                </div>
                                <div class="space-y-6 sm:space-y-5">
                                    <div
                                        class="rounded-lg border border-sky-100 bg-sky-50 px-4 py-3"
                                    >
                                        <label
                                            for="newsletter"
                                            class="flex items-start gap-3"
                                        >
                                            <input
                                                type="checkbox"
                                                v-model="
                                                    data.allow_marketing_emails
                                                "
                                                name="newsletter"
                                                id="newsletter"
                                                data-testid="profile-newsletter-checkbox"
                                                class="mt-0.5 h-4 w-4 rounded border-taupe-300"
                                            />
                                            <span class="text-sm text-taupe-800"
                                                >I wish to subscribe to the
                                                newsletter to receive tips and
                                                news occasionally. I know I can
                                                unsubscribe at any time.</span
                                            >
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            data-testid="profile-actions"
                            class="flex justify-end gap-3 border-t border-taupe-200 py-4"
                        >
                            <div class="flex justify-end gap-3">
                                <zen-button
                                    type="button"
                                    v-if="profileChanged"
                                    variant="secondary"
                                    @click="reset"
                                >
                                    Cancel
                                </zen-button>
                                <zen-button
                                    type="submit"
                                    variant="primary"
                                    :disabled="!profileChanged"
                                    :processing="saving"
                                >
                                    Save
                                </zen-button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    </main>
</template>

<script>
import moment from 'moment'
import ProfileHeaderTabs from '@/screens/_components/profile/HeaderTabs.vue'
import SignInMethods from '@/screens/_components/profile/SignInMethods.vue'
import { useSessionStore } from '@/stores/session'
import countries from '@/helpers/countries'
import { useDateFormatter } from '@/screens/_system/DateTime.vue'

import { errorMessage, successMessage } from '@/utils/notifications'

export default {
    name: 'ProfileDefaultScreen',
    components: {
        ProfileHeaderTabs,
        SignInMethods,
    },
    computed: {
        sessionStore() {
            return useSessionStore()
        },
        environments() {
            if (this.data.default_project_key === undefined) {
                return []
            }

            if (this.projects === undefined) {
                return []
            }

            if (
                this.projects.filter(
                    (p) => p.key == this.data.default_project_key
                ).length == 0
            ) {
                return []
            }

            return this.projects.filter(
                (p) => p.key == this.data.default_project_key
            )[0].environments.data
        },
        profileChanged() {
            return (
                JSON.stringify(this.normalizePreferences(this.data)) !==
                JSON.stringify(
                    this.normalizePreferences(this.sessionStore.data.user.data)
                )
            )
        },
        projects() {
            return this.sessionStore.data.projects.data
        },
    },
    data() {
        const { DATE_FORMATS, TIME_FORMATS } = useDateFormatter()
        return {
            saving: false,
            data: {},
            timezones: countries(),
            dateFormats: DATE_FORMATS,
            timeFormats: TIME_FORMATS,
        }
    },
    methods: {
        normalizePreferences(input) {
            const normalized = JSON.parse(JSON.stringify(input || {}))

            const normalizeOptionalValue = (value) => {
                if (
                    value === null ||
                    value === undefined ||
                    value === '' ||
                    value === 'null' ||
                    value === 'undefined'
                ) {
                    return null
                }

                return value
            }

            normalized.timezone = normalizeOptionalValue(normalized.timezone)
            normalized.date_format = normalizeOptionalValue(
                normalized.date_format
            )
            normalized.time_format = normalizeOptionalValue(
                normalized.time_format
            )

            return normalized
        },
        moment(datetime) {
            return moment(datetime)
        },
        reset() {
            this.data = this.normalizePreferences(
                this.sessionStore.data.user.data
            )
        },
        saveProfile() {
            this.saving = true
            const payload = this.normalizePreferences(this.data)

            this.sessionStore
                .updateUser(payload)
                .then(() => {
                    successMessage('Your profile has been updated!')
                    this.reset()
                })
                .catch((error) => {
                    errorMessage(error)
                })
                .finally(() => {
                    this.saving = false
                })
        },
    },
    mounted() {
        this.reset()
    },
}
</script>
